import { AxiosError, AxiosInstance } from "axios";
import { matchPath, RouteComponentProps } from "react-router-dom";

import {
	AuthToken,
	cache,
	QueryParamConstants,
	ZomentumRoutes,
	ZomentumStorageKeys
} from "@/Contracts";
import {
	ChildException,
	ExceptionType,
	ServerError,
	ServerValidationErrorResponse
} from "@/Components/Utils/ServerError";
import { registerAccessTokenRefresher } from "@/Components/Utils/accessTokenRefresher";
import ZMessage from "@molecules/ZMessage";
import { GlobalState } from "@/Reducers/interface";
import { fileReader } from "@/Utils/ProposalBuilder";
import {
	getAppPathname,
	prependBasePath
} from "@/Utils/routing/getAppPathname";
import { isEmbedded } from "@/Utils/superops/embedDetection";
import {
	isSuperOpsReauthReloadInProgress,
	markSuperOpsReauthReloadInProgress
} from "@/Utils/superops/superOpsReauthState";

export interface ApiInterceptorReportData {
	userData: GlobalState["user"]["userData"];
	currentActiveOpportunity: GlobalState["opportunities"]["currentActiveOpportunity"];
	currentActiveReport: GlobalState["reports"]["currentActiveReport"];
	currentActivePipeline: GlobalState["pipelines"]["currentActivePipeline"];
	currentActiveClient: GlobalState["clients"]["currentActiveClient"];
}

export interface ApiInterceptorDeps {
	getAccessToken: () => Promise<string>;
	logout: () => unknown;
	history: Pick<RouteComponentProps["history"], "push">;
	reportData: ApiInterceptorReportData;
}

export const installApiInterceptors = (
	instance: AxiosInstance,
	{ getAccessToken, logout, history, reportData }: ApiInterceptorDeps
): (() => void) => {
	const requestInterceptorId = instance.interceptors.request.use(
		request => request,
		error => {
			return Promise.reject(error);
		}
	);
	const responseInterceptorId = instance.interceptors.response.use(
		response => {
			return response;
		},
		async (error: AxiosError<ServerValidationErrorResponse>) => {
			if (
				error.request?.responseType === "blob" &&
				error.response?.data instanceof Blob &&
				error.response.data.type?.toLowerCase().indexOf("json") != -1
			) {
				const { data } = error.response;
				const file = await fileReader(data);
				error.response.data = JSON.parse(String(file));
			}

			const skipServerErrorBannerStatusCodes =
				error.config?.headers[
					"Skip-Server-Error-Banner-Status-Codes"
				] ?? "";

			if (
				error?.response?.status === 401 &&
				!skipServerErrorBannerStatusCodes.includes("401")
			) {
				if (
					(
						error.response.data?.child_exceptions?.map(
							ex => ex.exception_type
						) || new Array<ChildException>()
					)?.includes(ExceptionType.ReAuthorizeException)
				) {
					if (isSuperOpsReauthReloadInProgress()) {
						throw new ServerError(error);
					}
					const hint = cache.get(
						ZomentumStorageKeys.SUPEROPS_HINT,
						true
					);
					const alreadyRetried = cache.get(
						ZomentumStorageKeys.SUPEROPS_SSO_RETRIED,
						true
					);
					if (isEmbedded() && hint && !alreadyRetried) {
						markSuperOpsReauthReloadInProgress();
						cache.save(
							ZomentumStorageKeys.SUPEROPS_SSO_RETRIED,
							true
						);
						const region = cache.get(
							ZomentumStorageKeys.SUPEROPS_REGION,
							true
						);
						const retryUrl =
							prependBasePath(ZomentumRoutes.Authorize) +
							`?superops_hint=${encodeURIComponent(
								hint
							)}&embed=true` +
							(region
								? `&region=${encodeURIComponent(region)}`
								: "");
						window.location.replace(retryUrl);
						throw new ServerError(error);
					}
					ZMessage.success(error?.response?.data?.message, 2, () =>
						logout()
					);
					return;
				} else if (error?.config && !error.config.headers["retry"]) {
					const existingToken = cache.get(
						ZomentumStorageKeys.Token,
						true
					)?.token;
					if (!existingToken) {
						throw new ServerError(error);
					}
					let token = "";
					try {
						token = await getAccessToken();
					} catch (e) {
						console.warn(e);
						return logout();
					}
					const tokenInCache = existingToken;
					if (tokenInCache && token && token.length > 0) {
						error.config.headers[
							"Authorization"
						] = `Bearer ${token}`;
						error.config.headers["retry"] = true;
						cache.save(
							ZomentumStorageKeys.Token,
							new AuthToken(token)
						);
						return instance(error.config);
					} else {
						logout();
					}
				} else {
					logout();
				}
			}

			if (
				error?.response?.status === 403 &&
				!skipServerErrorBannerStatusCodes.includes("403")
			) {
				ZMessage.error("Unauthorized to perform the action");
				return;
			}

			const mergeEntityId =
				error?.response?.data?.error_details?.merged_entity_id;
			if (mergeEntityId) {
				let url = "";
				const pathname = getAppPathname();
				if (
					matchPath(pathname, ZomentumRoutes.ClientsDetail)?.isExact
				) {
					url = ZomentumRoutes.ClientsDetail.replace(
						":id",
						mergeEntityId
					);
				} else if (
					matchPath(pathname, ZomentumRoutes.OpportunityDetail)
						?.isExact
				) {
					url = ZomentumRoutes.OpportunityDetail.replace(
						":id",
						mergeEntityId
					);
				} else if (
					matchPath(pathname, ZomentumRoutes.DocumentsPreview)
						?.isExact
				) {
					if (
						window.location.search.includes(
							QueryParamConstants.CLIENT_USER_ID
						)
					) {
						url =
							window.location.pathname +
							"?" +
							QueryParamConstants.CLIENT_USER_ID +
							"=" +
							mergeEntityId;
					}
				}
				if (url) {
					history.push(url);
					window.location.reload();
					throw new ServerError(error);
				}
			}

			if (
				!skipServerErrorBannerStatusCodes
					.split(",")
					.includes(error?.response?.status?.toString())
			) {
				ZMessage.serverError({
					errorData: error,
					reportData,
					currentLocationPath: window.location.pathname
				});
			}

			if (
				error?.response?.status === 500 &&
				!error.config?.headers["Skip-500-Server-Error-Redirection"]
			) {
				history.push(ZomentumRoutes.Error);
			}
			throw new ServerError(error);
		}
	);
	const unregisterAccessTokenRefresher =
		registerAccessTokenRefresher(getAccessToken);

	return () => {
		instance.interceptors.request.eject(requestInterceptorId);
		instance.interceptors.response.eject(responseInterceptorId);
		unregisterAccessTokenRefresher();
	};
};
